import React, { KeyboardEvent, useEffect, useState } from "react";
import { FormikFieldProps } from "../formUtils";
import { AutocompleteOption, AutocompleteWrapper } from "../muiAutocomplete";
import { useTranslation } from "react-i18next";
import { getRoutes } from "../../../services/routeService";

interface RouteSelectProps extends Partial<FormikFieldProps> {
    required?: boolean;
    multiple?: boolean;
    small?: boolean;
    open?: boolean;
    defaultOpen?: boolean;
    label?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    onChange?: (id: string | string[] | null, title?: string | null) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
    onCreateRoute?: () => void;
}

const CREATE_ID = "__create_route__";

export const RouteSelect = (props: RouteSelectProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const {
        value,
        small,
        onChange,
        onBlur,
        onKeyDown,
        multiple,
        open,
        autoFocus,
        defaultOpen,
        onCreateRoute,
        ...rest
    } = props;

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        getRoutes()
            .then((res) => {
                if (!mounted) return;
                const base = (res || []).map((r) => ({ id: r?.id, title: r?.name })) as AutocompleteOption[];

                const createOption: AutocompleteOption = {
                    id: CREATE_ID,
                    title: t("route.newRoute"),
                    extra: "create",
                };

                const withCreate = !multiple && onCreateRoute ? [createOption, ...base] : base;
                setOptions(withCreate);
            })
            .catch(console.error)
            .finally(() => mounted && setLoading(false));

        return () => {
            mounted = false;
        };
    }, [multiple, onCreateRoute, t]);

    const handleChange = (val: AutocompleteOption | AutocompleteOption[] | null) => {
        if (!onChange) return;

        if (Array.isArray(val)) {
            const hasCreate = val.some((v) => v.id === CREATE_ID);
            if (hasCreate) {
                onCreateRoute?.();
            }
            const filtered = val.filter((v) => v.id !== CREATE_ID);
            onChange(filtered.map((v) => v.id));
            return;
        }

        if (val?.id === CREATE_ID) {
            onCreateRoute?.();
            return;
        }

        onChange(val?.id ?? null, val?.title ?? null);
    };

    return (
        <AutocompleteWrapper
            label={props.label || t("route.route")}
            value={value ?? ""}
            size={small ? "small" : "medium"}
            options={options}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            loading={loading}
            required={props.required}
            multiple={multiple}
            open={open}
            autoFocus={autoFocus}
            textFieldProps={{ ...rest }}
            disabled={props.disabled}
            defaultOpen={defaultOpen}
        />
    );
};